package com.dotdrives.web.pages.settings

import com.dotdrives.design.DotTone
import com.dotdrives.model.BillingStatus
import com.dotdrives.model.Budget
import com.dotdrives.model.OrgMember
import kotlin.math.round
import kotlin.time.Clock
import kotlin.time.Instant

// ---------------------------------------------------------------------------------------------
// Members / roles
// ---------------------------------------------------------------------------------------------

fun memberInitials(displayName: String): String =
    displayName.split(' ', '.', '_', '-')
        .filter(String::isNotBlank)
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "?" }

/**
 * The design names Admin, Standard, Field Representative, and Read-only. Only the first three
 * map onto real `org_members.role` values (`owner|admin|member|field`) — Read-only has no
 * home in that vocabulary and docs/schema.md's own open-questions section says so explicitly:
 * its permissions aren't specified precisely enough to build. This module never offers it.
 */
fun roleLabel(role: String): String = when (role) {
    "owner" -> "Owner"
    "admin" -> "Admin"
    "member" -> "Standard"
    "field" -> "Field Representative"
    else -> role.replaceFirstChar(Char::uppercase)
}

/**
 * admin/member/field copy is reproduced verbatim from design/portal-users-settings.dc.html's
 * `roleOptions` array. The design has no `owner` row at all (org creator only, assigned once
 * by `create_org` — never offered in the invite picker); that description is this module's own.
 */
fun roleDescription(role: String): String = when (role) {
    "owner" -> "Full control of this workspace, including ownership transfer"
    "admin" -> "Everything, including billing and settings"
    "member" -> "Drives, givers, donations — the fundraiser role"
    "field" -> "Submits updates only, no donor financials"
    else -> ""
}

/** Matches design/portal-users-settings.dc.html's per-role badge colors 1:1 with the shared DotTone palette. */
fun roleTone(role: String): DotTone = when (role) {
    "owner", "admin" -> DotTone.Primary
    "member" -> DotTone.Info
    "field" -> DotTone.Success
    else -> DotTone.Neutral
}

/**
 * Icon for the invite-role picker, keyed off `com.dotdrives.model.INVITABLE_ROLES` — the single
 * source of truth every invite-role UI and the controller's own validation both derive from
 * (see `INVITABLE_ROLES`'s doc comment in `shared/.../model/Settings.kt`). Adding a role there
 * automatically adds a picker option here; there is no second, independently-maintained list of
 * assignable roles anywhere in this module.
 */
fun roleIcon(role: String): String = when (role) {
    "admin" -> "👑"
    "member" -> "🧭"
    "field" -> "📣"
    else -> "•"
}

// ---------------------------------------------------------------------------------------------
// Real, DB-enforced permission summary. Deliberately narrower than the design's 8-row matrix
// (Drives/Givers/Donations/Giver updates/Giving forms/Users/Goals & budgets/Billing): this
// module only knows the true RLS shape of the three tables it owns (org_members/org_invites,
// budgets, subscriptions). Claiming a restriction on Givers/Donations/etc. this module doesn't
// enforce would be worse than not showing it at all (docs/schema.md's own open-questions
// section flags exactly that mismatch as unresolved) — so those rows are never rendered here.
// ---------------------------------------------------------------------------------------------

data class ModulePermission(val name: String, val canRead: Boolean, val canWrite: Boolean)

/** Matches current administrative writes and existing financial read permissions. */
fun permissionsForRole(role: String): List<ModulePermission> {
    val canWriteLikeMember = com.dotdrives.model.canAdminister(role)
    val canReadStrict = role != "field"
    return listOf(
        ModulePermission("Users", canRead = true, canWrite = canWriteLikeMember),
        ModulePermission("Goals & budgets", canRead = true, canWrite = canWriteLikeMember),
        ModulePermission("Billing", canRead = canReadStrict, canWrite = false),
    )
}

// ---------------------------------------------------------------------------------------------
// Budgets
// ---------------------------------------------------------------------------------------------

/** The current org-scope budget row — the one with the latest period_start. */
fun currentOrgBudget(budgets: List<Budget>): Budget? =
    budgets.filter { it.scope == "org" && !it.archived }.maxByOrNull(Budget::periodStart)

/** Latest steward-scope budget per scope_ref (org_members.user_id). */
fun currentStewardBudgets(budgets: List<Budget>): Map<String, Budget> =
    budgets.filter { it.scope == "steward" && it.scopeRef != null && !it.archived }
        .groupBy { it.scopeRef!! }
        .mapValues { (_, rows) -> rows.maxBy(Budget::periodStart) }

fun stewardBudgetRows(budgets: List<Budget>, members: List<OrgMember>): List<Pair<OrgMember, Budget>> {
    val byUser = currentStewardBudgets(budgets)
    return members.mapNotNull { member -> byUser[member.userId]?.let { member to it } }
}

fun phaseTemplateBudgetCount(budgets: List<Budget>): Int =
    budgets.count { it.scope == "phase_template" }

// ---------------------------------------------------------------------------------------------
// Money / dates. A value the user may not see (RLS-hidden, or simply absent) renders "—".
// ---------------------------------------------------------------------------------------------

private const val MONEY_HIDDEN = "—"

fun formatMoneyFull(amount: Double?): String {
    if (amount == null) return MONEY_HIDDEN
    val whole = round(amount).toLong()
    val sign = if (whole < 0) "-" else ""
    return "$sign$${groupThousands(kotlin.math.abs(whole))}"
}

fun formatMoneyCompact(amount: Double?): String {
    if (amount == null) return MONEY_HIDDEN
    val abs = kotlin.math.abs(amount)
    val sign = if (amount < 0) "-" else ""
    return when {
        abs >= 1_000_000 -> "$sign$${trimDecimal(abs / 1_000_000)}M"
        abs >= 1_000 -> "$sign$${trimDecimal(abs / 1_000)}K"
        else -> "$sign$${abs.toLong()}"
    }
}

private fun trimDecimal(value: Double): String {
    val rounded = round(value * 10) / 10
    val asLong = rounded.toLong()
    return if (rounded == asLong.toDouble()) asLong.toString() else rounded.toString()
}

private fun groupThousands(value: Long): String =
    value.toString().reversed().chunked(3).joinToString(",").reversed()

private val MONTH_NAMES = listOf(
    "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
)

/** "Oct 1, 2026". Null renders "—". */
fun formatDate(instant: Instant?): String {
    if (instant == null) return MONEY_HIDDEN
    val date = kotlin.js.Date(instant.toEpochMilliseconds().toDouble())
    val month = MONTH_NAMES.getOrElse(date.getMonth()) { "" }
    return "$month ${date.getDate()}, ${date.getFullYear()}"
}

/** "2026-01-01" from a plain `date` column, without the day-boundary risk of round-tripping through Instant. */
fun formatDateOnly(isoDate: String): String {
    val parts = isoDate.split("-")
    if (parts.size != 3) return isoDate
    val month = MONTH_NAMES.getOrElse(parts[1].toIntOrNull()?.minus(1) ?: -1) { return isoDate }
    val day = parts[2].toIntOrNull() ?: return isoDate
    return "$month $day, ${parts[0]}"
}

// ---------------------------------------------------------------------------------------------
// Billing banner — sourced from `public.billing_status()`, never `subscriptions` (see
// SettingsController / docs/schema.md's controller ruling on this module's permission shape).
// ---------------------------------------------------------------------------------------------

data class BillingBanner(val message: String, val tone: String)

/** null = nothing worth interrupting the user for (active/comped subscription). */
fun billingBanner(status: BillingStatus?, now: Instant = Clock.System.now()): BillingBanner? {
    if (status?.status == null) return null
    return when (status.status) {
        "trialing" -> {
            val endsAt = status.trialEndsAt ?: return null
            val days = (endsAt - now).inWholeDays
            when {
                days < 0 -> BillingBanner("Your trial ended ${formatDate(endsAt)}. Pick a plan to keep going.", "danger")
                days == 0L -> BillingBanner("Your trial ends today.", "warning")
                days <= 3 -> BillingBanner("Your trial ends in $days day${if (days == 1L) "" else "s"}.", "warning")
                else -> null
            }
        }
        "past_due" -> BillingBanner("Your last payment didn't go through. Update billing to avoid a pause.", "danger")
        "canceled" -> BillingBanner("Your subscription has been canceled.", "danger")
        else -> null
    }
}

/** Exact cents and explicit currency for budget management. */
fun formatBudgetMoney(amount: Double): String {
    val cents = round(kotlin.math.abs(amount) * 100).toLong()
    return "${if (amount < 0) "-" else ""}\$${groupThousands(cents / 100)}.${(cents % 100).toString().padStart(2, '0')} USD"
}
